// Playtest: pick two saved decks, run N Forge-simulated games, watch live W-L-D-DNF.
//
// Jobs are server-side singletons (one Forge run at a time, globally).
// Client disconnect does NOT abort the run — results land on disk
// regardless, so refresh is safe. On mount we probe /active and resume
// the SSE stream if a job is already in flight.

import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Globe, Swords } from "lucide-react";
import MatchResults from "../components/MatchResults";
import LiquidButton, { MANA_R } from "../components/ui/LiquidButton";
import {
  getActivePlaytest,
  getPlaytest,
  listDecks,
  listPublicDecks,
  startPlaytest,
  streamPlaytest,
  type PlaytestForgeMissingError,
  type PlaytestJob,
  type PlaytestOutcome,
} from "../lib/api";
import { useRefreshBalance } from "../lib/useBalance";
import { cn } from "../lib/cn";

// Composite picker id: "<owner>:<slug>". Lets the picker disambiguate
// owned-vs-public-with-same-slug and lets the playtest call know which
// owner to pass to the backend resolver.
function pickerId(owner: string, slug: string): string {
  return `${owner}:${slug}`;
}
function parsePickerId(id: string): { owner: string; slug: string } | null {
  const ix = id.indexOf(":");
  if (ix < 0) return null;
  return { owner: id.slice(0, ix), slug: id.slice(ix + 1) };
}

interface PickerOption {
  id: string; // composite "<owner>:<slug>"
  slug: string;
  owner: string;
  theme: string;
  brief: string;
  /** undefined = caller owns this row; otherwise the owner's username
   *  for the public-deck path. */
  publicOwner?: string;
  isMeta: boolean;
}

// Games options. 10 is the default — ~1 minute, enough signal to not
// be pure noise, cheap to rerun. 50 is the upper end for a "I really
// want to believe this" run.
const GAME_OPTIONS = [5, 10, 25, 50] as const;
type GameCount = (typeof GAME_OPTIONS)[number];

// ~6-10s per game is the rough Forge benchmark. We show a pessimistic
// estimate so the user isn't surprised when 50 games takes 8 minutes.
const SEC_PER_GAME_EST = 9;

function estimateSeconds(games: number): number {
  return games * SEC_PER_GAME_EST;
}

function fmtSeconds(s: number): string {
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem === 0 ? `${m}m` : `${m}m ${rem}s`;
}

export default function Playtest() {
  const refreshBalance = useRefreshBalance();
  const decksQuery = useQuery({
    queryKey: ["decks"],
    queryFn: () => listDecks(),
  });
  // Public decks (incl. seeded gauntlet meta archetypes). Always
  // fetched on mount so the picker can offer them — they're the
  // most useful "play against the meta" affordance.
  const publicDecksQuery = useQuery({
    queryKey: ["public-decks"],
    queryFn: () => listPublicDecks(50, 0),
    staleTime: 60_000,
  });

  // On page load, check for an in-flight job and auto-resume its stream.
  const activeQuery = useQuery({
    queryKey: ["playtest-active"],
    queryFn: getActivePlaytest,
    // No refetch interval — we'll actively manage the stream once resumed.
    staleTime: 0,
  });

  const [deckAId, setDeckAId] = useState("");
  const [deckBId, setDeckBId] = useState("");
  const [games, setGames] = useState<GameCount>(10);

  const [jobId, setJobId] = useState<string | null>(null);
  const [job, setJob] = useState<PlaytestJob | null>(null);
  const [live, setLive] = useState<PlaytestOutcome | null>(null);
  const [completedGames, setCompletedGames] = useState(0);
  const [startError, setStartError] = useState<Error | null>(null);
  const [forgeMissing, setForgeMissing] = useState<PlaytestForgeMissingError | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [nowMs, setNowMs] = useState<number>(Date.now());
  const streamDisposerRef = useRef<(() => void) | null>(null);

  // Resume active job once on mount. If one is running, fetch its state
  // and wire up the stream so we don't start a second one.
  useEffect(() => {
    const activeId = activeQuery.data?.active_job_id;
    if (!activeId || jobId) return;
    (async () => {
      try {
        const existing = await getPlaytest(activeId);
        setJobId(existing.id);
        setJob(existing);
        setLive(existing.outcome);
        setCompletedGames(
          existing.outcome.games_played + existing.outcome.dnf,
        );
        subscribe(existing.id);
      } catch {
        // Active job referenced but /:id missing — shouldn't happen, but
        // don't crash the page over it.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeQuery.data]);

  // Elapsed-time ticker. Only ticks while a job is running to avoid
  // re-rendering after completion.
  useEffect(() => {
    if (!job || job.status !== "running") return;
    const h = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(h);
  }, [job]);

  // Clean up SSE subscription on unmount.
  useEffect(() => {
    return () => streamDisposerRef.current?.();
  }, []);

  const subscribe = (id: string) => {
    streamDisposerRef.current?.();
    streamDisposerRef.current = streamPlaytest(id, (msg) => {
      if (msg.kind === "start") {
        setJob(msg.data);
        setLive(msg.data.outcome);
      } else if (msg.kind === "progress") {
        setLive(msg.data.outcome);
        setCompletedGames(msg.data.completed);
      } else if (msg.kind === "done") {
        setJob(msg.data);
        setLive(msg.data.outcome);
        setCompletedGames(
          msg.data.outcome.games_played + msg.data.outcome.dnf,
        );
        // Match settled — refresh sidebar balance.
        refreshBalance();
      } else if (msg.kind === "error") {
        setStartError(new Error(msg.data.error));
        // Server refunded the pre-charge on failure.
        refreshBalance();
      }
    });
  };

  const runMatch = async () => {
    if (!deckAId || !deckBId) return;
    const a = parsePickerId(deckAId);
    const b = parsePickerId(deckBId);
    if (!a || !b) {
      setStartError(new Error("invalid deck selection"));
      return;
    }
    setStartError(null);
    setForgeMissing(null);
    setJob(null);
    setLive(null);
    setCompletedGames(0);
    setSubmitting(true);
    try {
      const { job_id } = await startPlaytest({
        deck_a_id: a.slug,
        deck_b_id: b.slug,
        // Only include *_owner when it's a public deck — owned decks
        // resolve via the existing owner-scoped path with no owner
        // override. Lets the backend distinguish which lookup to use.
        ...(optA?.publicOwner ? { deck_a_owner: optA.publicOwner } : {}),
        ...(optB?.publicOwner ? { deck_b_owner: optB.publicOwner } : {}),
        games,
      });
      setJobId(job_id);
      // Pre-charge landed server-side; reflect it immediately.
      refreshBalance();
      subscribe(job_id);
    } catch (e) {
      const err = e as Error & { forgeMissing?: PlaytestForgeMissingError };
      if (err.forgeMissing) {
        setForgeMissing(err.forgeMissing);
      } else {
        setStartError(err);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const myDecks = decksQuery.data?.decks ?? [];
  const publicDecks = publicDecksQuery.data?.decks ?? [];
  // Build the unified picker option list: owned decks, then public
  // decks (excluding any that match an owned slug+owner — shouldn't
  // happen, but cheap to dedup defensively).
  const options = useMemo<PickerOption[]>(() => {
    const opts: PickerOption[] = [];
    for (const d of myDecks) {
      const owner = d.owner_username ?? "";
      opts.push({
        id: pickerId(owner, d.id),
        slug: d.id,
        owner,
        theme: d.theme,
        brief: d.brief,
        isMeta: d.role === "meta_reference",
      });
    }
    const seen = new Set(opts.map((o) => o.id));
    for (const d of publicDecks) {
      const owner = d.owner_username ?? "";
      if (!owner) continue;
      const key = pickerId(owner, d.id);
      if (seen.has(key)) continue;
      opts.push({
        id: key,
        slug: d.id,
        owner,
        theme: d.theme,
        brief: d.brief,
        publicOwner: owner,
        isMeta: d.role === "meta_reference",
      });
    }
    return opts;
  }, [myDecks, publicDecks]);

  const findOpt = (id: string): PickerOption | null =>
    options.find((o) => o.id === id) ?? null;
  const optA = useMemo(() => findOpt(deckAId), [options, deckAId]);
  const optB = useMemo(() => findOpt(deckBId), [options, deckBId]);
  const sameDeck = deckAId && deckAId === deckBId;
  const canRun =
    !!deckAId && !!deckBId && !submitting && (!job || job.status !== "running");

  const running = job?.status === "running";
  const elapsedMs = job ? nowMs - new Date(job.started_at).getTime() : 0;

  return (
    <div className="mx-auto max-w-5xl px-6 md:px-10 py-10 space-y-8">
      <header>
        <div className="eyebrow">Playtest</div>
        <h1 className="mt-2 font-display text-3xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.0]">
          Two decks enter.
          <br />
          <span className="foil">One deck wins.</span>
        </h1>
        <p className="text-sm text-fg-muted mt-5 max-w-xl leading-relaxed">
          Pick two saved decks and run N games through Forge's headless rules
          engine. Raw win counts with a 95% confidence interval — small sample
          sizes have wide error bars. Runs continue if you navigate away.
        </p>
      </header>

      {forgeMissing && <ForgeMissingBanner payload={forgeMissing} />}

      <section className="surface p-6 space-y-5">
        <div className="grid gap-5 md:grid-cols-2">
          <DeckPicker
            label="Deck A"
            value={deckAId}
            onChange={setDeckAId}
            options={options}
            disabledIds={deckBId && !sameDeck ? new Set([deckBId]) : undefined}
            summary={optA}
          />
          <DeckPicker
            label="Deck B"
            value={deckBId}
            onChange={setDeckBId}
            options={options}
            disabledIds={deckAId && !sameDeck ? new Set([deckAId]) : undefined}
            summary={optB}
          />
        </div>

        {sameDeck && (
          <div className="text-[11px] text-warn">
            Both sides are the same deck. Mirror matches are valid but any
            skew beyond 50/50 is pure variance.
          </div>
        )}

        <div className="flex flex-wrap items-end gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-fg-subtle mb-1.5">
              Games
            </div>
            <div className="flex gap-1">
              {GAME_OPTIONS.map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setGames(n)}
                  className={cn(
                    "px-3 py-1.5 text-sm font-mono tabular-nums rounded-md border transition-colors",
                    games === n
                      ? "border-accent bg-accent/10 text-fg"
                      : "border-border text-fg-muted hover:text-fg hover:bg-bg-raised/50",
                  )}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
          <div className="text-[11px] text-fg-subtle font-mono">
            ~{fmtSeconds(estimateSeconds(games))} est.
          </div>
          <div className="w-full flex justify-center sm:w-auto sm:ml-auto sm:block">
            <LiquidButton
              className="h-10 text-sm"
              disabled={!canRun}
              onClick={runMatch}
              palette={MANA_R}
              trailing={
                !submitting && !running ? (
                  <span className="text-[10px] font-mono opacity-70">· Free</span>
                ) : undefined
              }
            >
              <Swords size={14} strokeWidth={1.75} />
              {submitting ? "Starting..." : running ? "Running..." : "Run match"}
            </LiquidButton>
          </div>
        </div>

        {startError && (
          <div className="text-xs text-danger">{startError.message}</div>
        )}
      </section>

      {job && (
        <MatchResults
          job={job}
          live={live ?? job.outcome}
          completedGames={completedGames}
          elapsedMs={elapsedMs}
        />
      )}
    </div>
  );
}

function DeckPicker({
  label,
  value,
  onChange,
  options,
  disabledIds,
  summary,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: PickerOption[];
  disabledIds?: Set<string>;
  summary: PickerOption | null;
}) {
  // Group options into three buckets so the dropdown reads as
  // "your decks" → "meta archetypes" → "other public decks".
  // Meta archetypes get pride of place because they're the most
  // useful playtest target.
  const mine = options.filter((o) => !o.publicOwner);
  const meta = options.filter((o) => o.publicOwner && o.isMeta);
  const otherPublic = options.filter((o) => o.publicOwner && !o.isMeta);

  const renderOpt = (o: PickerOption) => (
    <option
      key={o.id}
      value={o.id}
      disabled={disabledIds?.has(o.id) ?? false}
    >
      {o.theme}
      {o.publicOwner && !o.isMeta ? ` — by ${o.publicOwner}` : ""}
    </option>
  );

  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-fg-subtle mb-1.5">
        {label}
      </div>
      <select
        className="input w-full"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">— pick a deck —</option>
        {mine.length > 0 && (
          <optgroup label="Your decks">
            {mine.map(renderOpt)}
          </optgroup>
        )}
        {meta.length > 0 && (
          <optgroup label="Meta archetypes (gauntlet)">
            {meta.map(renderOpt)}
          </optgroup>
        )}
        {otherPublic.length > 0 && (
          <optgroup label="Other public decks">
            {otherPublic.map(renderOpt)}
          </optgroup>
        )}
      </select>
      {summary && (
        <div className="mt-2 space-y-1">
          {summary.publicOwner && (
            <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-fg-subtle">
              <Globe size={10} strokeWidth={2} />
              {summary.isMeta ? "meta archetype" : `public · by ${summary.publicOwner}`}
            </div>
          )}
          <div className="text-xs text-fg-muted line-clamp-2">
            {summary.brief}
          </div>
        </div>
      )}
    </div>
  );
}


function ForgeMissingBanner({ payload }: { payload: PlaytestForgeMissingError }) {
  return (
    <div className="surface p-5 space-y-3 border border-warn/40">
      <div className="text-sm font-semibold text-warn">Forge not installed</div>
      <div className="text-xs text-fg-muted">
        The playtest endpoint looked for Forge in these locations and came up empty:
      </div>
      <ul className="text-[11px] font-mono text-fg-subtle space-y-0.5">
        {payload.checked.map((p) => (
          <li key={p}>· {p}</li>
        ))}
      </ul>
      <pre className="whitespace-pre-wrap break-words text-[11px] leading-relaxed text-fg-muted font-mono border-t border-border pt-3">
        {payload.install_hint}
      </pre>
    </div>
  );
}
